// Mailing panel for the event dialog (DCP-2228 follow-up)
//
// Read-only: it shows the catalogue mailing(s) the event *can* be advertised
// in — computed from the mailing dates, the event's start date, and (for the
// second one) whether the selected VA type has `hasExtraPeriod`. There is no
// stored event↔mailing relation shown here; that snapshot
// (DepotEvent.availableMailings) is taken once at creation time on the
// backend and is a separate concern from this live display.
//
// Shown in create mode too, computed against the form field rather than a
// saved record, so the result appears as soon as a date is picked.

import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { Check, AlertCircle } from 'lucide-react';
import type { Mailing } from '@/types';
import { findMatchingMailings, formatDay, type MailingMatch } from './mailing-match';

interface MailingPanelProps {
  mailings: Mailing[];
  /** ISO datetime from the form; empty while a new event has no date yet. */
  startAt: string | undefined;
  /** Whether the currently selected VA type also matches via period 2. */
  hasExtraPeriod: boolean;
  isLoading?: boolean;
  t: TFunction<'events'>;
}

export function MailingPanel({ mailings, startAt, hasExtraPeriod, isLoading, t }: MailingPanelProps) {
  // `new Date()` in a memo keyed on the inputs: one render is internally
  // consistent, and the panel does not need to tick over midnight.
  const matches = useMemo(
    () => findMatchingMailings(mailings, startAt, new Date(), hasExtraPeriod),
    [mailings, startAt, hasExtraPeriod]
  );

  if (isLoading) {
    return <p className="text-xs text-muted-foreground">{t('mailings.loading')}</p>;
  }

  if (!startAt) {
    return <p className="text-xs text-muted-foreground">{t('mailings.needsDate')}</p>;
  }

  if (mailings.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('mailings.empty')}</p>;
  }

  if (matches.length === 0) {
    return (
      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
        {t('mailings.noneEligible')}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {matches.map((match) => (
        <MailingRow key={match.mailing.key} match={match} />
      ))}
    </div>
  );
}

function MailingRow({ match }: { match: MailingMatch }) {
  const { mailing, matchedPeriod } = match;
  const from = matchedPeriod === 'period1' ? mailing.period1From : mailing.period2From;
  const to = matchedPeriod === 'period1' ? mailing.period1To : mailing.period2To;

  return (
    <div className="flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <Check className="h-4 w-4 shrink-0 text-green-600" aria-hidden />
        <span className="truncate font-medium">{mailing.title}</span>
      </div>
      {/* Zeitraum-1/2-Beschriftung entfernt (29.09.2026, Nutzervorgabe) — welcher
          Zeitraum traf, ist intern relevant (matchedPeriod steuert weiterhin die
          Datumswahl oben), nach außen zeigt die Zeile nur noch das Datum. */}
      <div className="shrink-0 text-right text-xs text-muted-foreground">
        {from && to ? `${formatDay(from)}–${formatDay(to)}` : '–'}
      </div>
    </div>
  );
}
